import { useEffect, useMemo, useRef, useState } from "react";

export interface RollingNumberProps {
  value: number;
  format: (value: number) => string;
  durationMs?: number;
  className?: string;
}

export function RollingNumber({
  value,
  format,
  durationMs = 720,
  className,
}: RollingNumberProps) {
  const target = useMemo(() => format(value), [format, value]);
  const [animatedDisplay, setAnimatedDisplay] = useState(target);
  const rafRef = useRef<number>(0);
  const display = durationMs <= 0 ? target : animatedDisplay;

  useEffect(() => {
    if (durationMs <= 0) return;

    const digitIndexes: number[] = [];
    for (let i = 0; i < target.length; i += 1) {
      if (/\d/.test(target[i])) digitIndexes.push(i);
    }

    if (digitIndexes.length === 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setAnimatedDisplay(target);
      return;
    }

    const start = performance.now();

    const frame = (now: number) => {
      const t = Math.min((now - start) / durationMs, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      const resolvedDigits = Math.floor(eased * digitIndexes.length);

      if (t < 1) {
        const chars = target.split("");
        for (let i = 0; i < digitIndexes.length; i += 1) {
          const idx = digitIndexes[i];
          if (i >= resolvedDigits) {
            chars[idx] = Math.floor(Math.random() * 10).toString();
          }
        }
        setAnimatedDisplay(chars.join(""));
        rafRef.current = requestAnimationFrame(frame);
      } else {
        setAnimatedDisplay(target);
      }
    };

    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(rafRef.current);
    };
  }, [target, durationMs]);

  return <span className={className}>{display}</span>;
}
